import { CampfireRichEditor } from '@campfire/campfire-rich-editor/lib';
import { TabletButton } from '@campfire/tablet-button/lib';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@material-ui/core';
import { convertFromRaw, convertToRaw, EditorState } from 'draft-js';
import { FieldArrayRenderProps, Form, Formik, useFormikContext } from 'formik';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { object as YupObject, string as YupString } from 'yup';
import { FormBuilderValues } from '../FormBuilder';
import { GetForm_vm_form_formItems_VOLUNTEER_FormItemContentType as ContentFormItemType } from '../../__generated__/GetForm';

const validationSchema = YupObject().shape({
  content: YupString().required('Please provide some content'),
});

interface Props {
  open: boolean;
  close: () => void;
  arrayHelpers: FieldArrayRenderProps;
  data: ContentFormItemType | null;
}

export type ContentValues = {
  content: string;
  order: number;
  formItemId: string;
};

export const ContentFormItemDialog = memo((props: Props) => {
  const { open, close, data } = props;
  const formikContext = useFormikContext<FormBuilderValues>();
  const { values } = formikContext;
  const { formItems } = values;

  const editorContent = useMemo(() => (data?.content ? convertFromRaw(JSON.parse(data.content)) : undefined), [data]);
  const editorContentCreator = useMemo(
    () => (editorContent ? EditorState.createWithContent(editorContent) : EditorState.createEmpty()),
    [editorContent]
  );
  const [editorState, setEditorState] = useState(editorContentCreator);

  useEffect(() => {
    setEditorState(editorContentCreator);
  }, [open]);
  const hasRichContent = useMemo(() => editorState.getCurrentContent().hasText(), [editorState]);

  const title = useMemo(() => (data ? 'Edit Content' : 'Add Content'), [data]);

  const initialValues: ContentValues = useMemo(() => {
    return {
      formItemId: data?.formItemId ?? `new-${uuidv4()}`,
      content: data?.content ?? '',
      order: data?.order ?? formikContext.values.formItems.allIds.length,
      __typename: 'VOLUNTEER_FormItemContentType',
    };
  }, [data, open]);

  const handleSubmit = (vals: ContentValues) => {
    return data ? editContent(vals) : addContent(vals);
  };

  const addContent = (vals: ContentValues) => {
    formikContext.setFieldValue('formItems', {
      ...formItems,
      allIds: formItems.allIds.concat(vals.formItemId),
      byId: {
        ...formItems.byId,
        [vals.formItemId]: vals,
      },
    });
    close();
  };

  const editContent = (vals: ContentValues) => {
    formikContext.setFieldValue('formItems', {
      ...formItems,
      byId: {
        ...formItems.byId,
        [vals.formItemId]: vals,
      },
    });
    close();
  };

  const removeContent = () => {
    const optionId = formItems.byTriggerOption[initialValues.formItemId];
    formikContext.setFieldValue('formItems', {
      ...formItems,
      allIds: formItems.allIds.filter((itemId) => itemId !== initialValues.formItemId),
      byTrigger: optionId
        ? formItems.byTrigger[optionId].triggerItemIds.filter((itemId) => itemId !== initialValues.formItemId)
        : formItems.byTrigger,
    });
    close();
  };

  return (
    <Dialog open={open} keepMounted={false} maxWidth='sm' fullWidth>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ submitForm, setFieldValue }) => (
          <Form>
            <DialogTitle>
              <Typography variant='h6'>{title}</Typography>
              <Typography variant='body2' color='textSecondary'>
                {'Use content to break your form up into smaller sections'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <CampfireRichEditor
                placeholder='Write down your text here'
                editorState={editorState}
                setEditorState={(eState) => {
                  setEditorState(eState);
                  setFieldValue('content', JSON.stringify(convertToRaw(eState.getCurrentContent())));
                }}
              />
            </DialogContent>
            <DialogActions>
              <Grid container justify='flex-end'>
                {data ? (
                  <Grid item xs>
                    <TabletButton variant='outlined' color='error' onClick={removeContent}>
                      {'Delete'}
                    </TabletButton>
                  </Grid>
                ) : null}
                <Grid item>
                  <TabletButton onClick={close}>{'Cancel'}</TabletButton>
                </Grid>
                <Grid item>
                  <TabletButton
                    type='button'
                    color='primary'
                    variant='contained'
                    disabled={!hasRichContent}
                    onClick={submitForm}
                  >
                    {'Done'}
                  </TabletButton>
                </Grid>
              </Grid>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
});
